import { useQuery } from '@tanstack/react-query';
import { HeroPdpPageQueryKeys } from '@enums/react-query-keys';
import mapCatalogHitToProduct, { mapSimilarProductToProduct } from '@helpers/utils/catalog/map-catalog-hit-to-product';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { Preferences } from '@interfaces/models/preferences';
import { ElasticSearch } from '@interfaces/models/elasticSearch';
import SearchService from '@services/search-service';
import { mapCountryToTheSizeType, parseURLHashes } from '@helpers/utils/catalog/catalog-url-helpers';
import { useMemo } from 'react';
import { usePreferences } from '@context/preferences.context';
import logger from '@helpers/utils/logger/client';
import ProductService from '@services/product-service';
import { useBucket } from '@context/bucket.context';
import { LDState } from '@components/bucketTest/types';

interface HeroPdpSimilarProductQuery {
  similarProducts: CatalogProduct[];
  showMoreVisible: boolean;
  productFeedCategory: 'hero_pdp_recommend' | 'hero_pdp_search';
}

export const getHeroPdpSimilarProductsQueryFn =
  ({
    isSimilarProductsVariantsEnabled,
    preferences,
    productId,
    searchPayload,
  }: {
    isSimilarProductsVariantsEnabled: boolean;
    preferences: Preferences;
    productId: string | undefined;
    searchPayload: Partial<ElasticSearch>;
  }): (() => Promise<HeroPdpSimilarProductQuery>) =>
  async () => {
    // if similar products variants is enabled, we get the products from the recommendation service
    if (isSimilarProductsVariantsEnabled && !!productId) {
      const similarItems = await ProductService.listSimilarProducts({
        productId,
        limit: 36,
        disableSizeFilter: true,
      });

      // in the rare case that we have less than 12 items, we fall back to the search service
      if (similarItems.length >= 12) {
        const catalogProducts = similarItems.map((product) => mapSimilarProductToProduct(product));
        return { similarProducts: catalogProducts, showMoreVisible: true, productFeedCategory: 'hero_pdp_recommend' };
      } else {
        logger.info(
          `HeroPDP productID:${productId} - Not enough similar items (${similarItems.length}) from the recommendation service, falling back to search service`,
        );
      }
    }
    // if similar products variants is disabled, or there are not enough similar products from the recommendation service, we get the products from the search service
    const searchResponse = await SearchService.productSearch(searchPayload);
    const catalogProducts = (searchResponse?.items ?? []).map((product) =>
      mapCatalogHitToProduct(product, preferences.country, preferences.currency, preferences.language),
    );
    const showMoreVisible = catalogProducts.length > 0 && searchResponse?.paginationStats?.totalPages > 1;
    return { similarProducts: catalogProducts, showMoreVisible, productFeedCategory: 'hero_pdp_search' };
  };

const useHeroPdpSimilarProductsQuery = ({ productId }: { productId: string | undefined }) => {
  const { isFeatureEnabled, state } = useBucket();
  const ldLoading = [LDState.INITIALIZING, LDState.PENDING].includes(state);
  const isSimilarProductsVariantsEnabled = isFeatureEnabled((f) => !!f.enableRecommendationsOnHeroPdp, true);

  const filters = useMemo(
    () => (typeof window !== 'undefined' ? parseURLHashes(window.location.href)?.filters : null),
    [],
  );
  const preferences = usePreferences();

  const searchPayload: Partial<ElasticSearch> = useMemo(
    () => ({
      sortBy: 'relevance',
      pagination: { offset: 0, limit: 36 },
      locale: {
        country: preferences.country,
        currency: preferences.currency,
        language: preferences.language,
        sizeType: mapCountryToTheSizeType(preferences.regionName),
      },
      filters,
      mySizes: null,
    }),
    [preferences, filters],
  );

  const similarProductsQueryFn = useMemo(
    () =>
      getHeroPdpSimilarProductsQueryFn({
        isSimilarProductsVariantsEnabled,
        preferences,
        productId,
        searchPayload,
      }),
    [isSimilarProductsVariantsEnabled, preferences, productId, searchPayload],
  );

  return useQuery<HeroPdpSimilarProductQuery>({
    queryKey: [HeroPdpPageQueryKeys.GET_SIMILAR_PRODUCTS],
    queryFn: similarProductsQueryFn,
    enabled: !ldLoading,
  });
};

export default useHeroPdpSimilarProductsQuery;
